/**
 * One fetch client for every Robotensor service behind the gateway (`/api/<service>/v1/…`).
 *
 * - The access token lives in memory only (never localStorage). It is short-lived (10 min).
 * - The refresh token is an httpOnly cookie scoped to `/api/platform/v1/auth`; refresh needs the
 *   `x-rt-csrf: 1` header, which a cross-site form can't send.
 * - Refresh is transparent and single-flight: a request that gets 401 refreshes once and retries;
 *   concurrent 401s share one refresh. Tokens about to expire are refreshed before the request.
 * - Every request carries an `X-Request-ID`; errors keep it so people can quote it to support.
 * - Errors are problem+json → ApiError with a stable `code`.
 */
import { ApiError, CLIENT_CODES, type Problem } from './errors.ts'

export type Service = 'platform' | 'realdata' | 'realtosim' | 'sim'

export interface SessionUser {
  id: string
  display_name: string
  handle: string | null
  email: string | null
  email_verified: boolean
  roles: string[]
  tier: string
  locale: string
  country: string | null
  avatar_url: string | null
  onboarding: Record<string, unknown>
  created_at: string | null
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: SessionUser
}

export interface Session {
  accessToken: string
  /** Epoch ms when the access token expires. */
  expiresAt: number
  user: SessionUser
}

export type SessionEvent = 'signed-in' | 'refreshed' | 'signed-out' | 'expired' | 'user-updated'

export interface ClientOptions {
  /** Prefix for all paths, e.g. '' (same origin) or 'https://app.robotensor.ai'. */
  baseUrl?: string
  fetch?: typeof fetch
  now?: () => number
  requestId?: () => string
  /** Refresh this long before expiry (ms). */
  refreshSkewMs?: number
}

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[]

export interface RequestOptions {
  query?: Record<string, QueryValue>
  body?: unknown
  signal?: AbortSignal
  /** Send the bearer token and refresh on 401 (default true). */
  auth?: boolean
  headers?: Record<string, string>
}

type Listener = (session: Session | null, event: SessionEvent) => void

/** 401 codes that a fresh access token can fix. Others (e.g. a bad sign-in code) are final. */
const REFRESHABLE = new Set(['token_expired', 'token_invalid', 'signin_required', 'unauthorized'])

export function servicePrefix(service: Service): string {
  return `/api/${service}`
}

export function buildQuery(query?: Record<string, QueryValue>): string {
  if (!query) return ''
  const u = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) u.set(k, v.join(','))
    } else u.set(k, String(v))
  }
  const s = u.toString()
  return s ? `?${s}` : ''
}

function defaultRequestId(): string {
  const b = new Uint8Array(8)
  globalThis.crypto.getRandomValues(b)
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
}

export class ApiClient {
  private session: Session | null = null
  private refreshing: Promise<Session | null> | null = null
  private listeners = new Set<Listener>()
  private readonly baseUrl: string
  private readonly fetchImpl: typeof fetch
  private readonly now: () => number
  private readonly newRequestId: () => string
  private readonly skew: number

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? '').replace(/\/$/, '')
    this.fetchImpl = opts.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a))
    this.now = opts.now ?? Date.now
    this.newRequestId = opts.requestId ?? defaultRequestId
    this.skew = opts.refreshSkewMs ?? 30_000
  }

  // ── session ────────────────────────────────────────────────────────────────

  getSession(): Session | null {
    return this.session
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit(event: SessionEvent) {
    for (const l of this.listeners) l(this.session, event)
  }

  /** Adopt a token response (sign-in verify, token exchange). */
  setSession(t: TokenResponse, event: SessionEvent = 'signed-in'): Session {
    this.session = { accessToken: t.access_token, expiresAt: this.now() + t.expires_in * 1000, user: t.user }
    this.emit(event)
    return this.session
  }

  /** Update the cached user after a profile change (no token change). */
  updateUser(user: SessionUser) {
    if (!this.session) return
    this.session = { ...this.session, user }
    this.emit('user-updated')
  }

  clearSession(event: SessionEvent = 'signed-out') {
    const had = this.session !== null
    this.session = null
    if (had || event === 'expired') this.emit(event)
  }

  /**
   * Exchange the refresh cookie for a new access token. Single-flight. Resolves null when the
   * person is signed out (no/expired/revoked cookie); throws on network failure so a flaky
   * connection never signs anyone out.
   */
  refresh(): Promise<Session | null> {
    if (this.refreshing) return this.refreshing
    const run = (async () => {
      try {
        const t = await this.raw<TokenResponse>('POST', `${servicePrefix('platform')}/v1/auth/refresh`, {
          auth: false,
          headers: { 'x-rt-csrf': '1' },
        })
        return this.setSession(t, this.session ? 'refreshed' : 'signed-in')
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          this.clearSession(this.session ? 'expired' : 'signed-out')
          return null
        }
        throw e
      } finally {
        this.refreshing = null
      }
    })()
    this.refreshing = run
    return run
  }

  /** On app start: restore a session from the refresh cookie, if there is one. */
  restore(): Promise<Session | null> {
    return this.session ? Promise.resolve(this.session) : this.refresh()
  }

  async signOut(): Promise<void> {
    try {
      if (this.session) await this.request('platform', 'POST', '/v1/auth/signout', { body: {} })
    } catch {
      /* the server may already consider us signed out; the local session ends regardless */
    } finally {
      this.clearSession('signed-out')
    }
  }

  // ── requests ───────────────────────────────────────────────────────────────

  /** `client.request('platform', 'GET', '/v1/me')` → JSON body typed as T. */
  async request<T>(service: Service, method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const url = `${servicePrefix(service)}${path}`
    const auth = opts.auth !== false
    let refreshed = false
    if (auth && (!this.session || this.session.expiresAt - this.now() < this.skew)) {
      refreshed = true
      await this.refresh().catch(() => null)
    }
    const tokenUsed = this.session?.accessToken
    try {
      return await this.raw<T>(method, url, opts)
    } catch (e) {
      if (auth && e instanceof ApiError && e.status === 401 && REFRESHABLE.has(e.code)) {
        // Another request may already have refreshed: retry with its token instead of refreshing again.
        if (this.session && this.session.accessToken !== tokenUsed) return this.raw<T>(method, url, opts)
        if (!refreshed || tokenUsed) {
          const s = await this.refresh()
          if (s && s.accessToken !== tokenUsed) return this.raw<T>(method, url, opts)
        }
      }
      throw e
    }
  }

  get<T>(service: Service, path: string, opts?: Omit<RequestOptions, 'body'>) {
    return this.request<T>(service, 'GET', path, opts)
  }

  post<T>(service: Service, path: string, body?: unknown, opts?: RequestOptions) {
    return this.request<T>(service, 'POST', path, { ...opts, body: body ?? {} })
  }

  put<T>(service: Service, path: string, body?: unknown, opts?: RequestOptions) {
    return this.request<T>(service, 'PUT', path, { ...opts, body: body ?? {} })
  }

  patch<T>(service: Service, path: string, body?: unknown, opts?: RequestOptions) {
    return this.request<T>(service, 'PATCH', path, { ...opts, body: body ?? {} })
  }

  delete<T>(service: Service, path: string, opts?: RequestOptions) {
    return this.request<T>(service, 'DELETE', path, opts)
  }

  /** A binary resource (an image) with the session's token, as a Blob. Throws ApiError on a non-2xx answer. */
  async blob(service: Service, path: string): Promise<Blob> {
    if (!this.session || this.session.expiresAt - this.now() < this.skew) await this.refresh().catch(() => null)
    const headers: Record<string, string> = { 'x-request-id': this.newRequestId() }
    if (this.session) headers.authorization = `Bearer ${this.session.accessToken}`
    const url = `${servicePrefix(service)}${path}`
    const res = await this.fetchImpl(`${this.baseUrl}${url}`, { headers, credentials: 'same-origin' })
    if (!res.ok) throw new ApiError({ status: res.status, code: `http_${res.status}`, detail: '', requestId: headers['x-request-id']!, method: 'GET', path: url })
    return res.blob()
  }

  /** Probe a service's readiness endpoint (`/api/<svc>/readyz`): never throws. */
  async probe(service: Service, signal?: AbortSignal): Promise<{ ok: boolean; status: number | 'unreachable'; ms: number; body: unknown }> {
    const t0 = typeof performance !== 'undefined' ? performance.now() : this.now()
    try {
      const res = await this.fetchImpl(`${this.baseUrl}${servicePrefix(service)}/readyz`, { headers: { accept: 'application/json', 'x-request-id': this.newRequestId() }, signal, cache: 'no-store' })
      const ms = (typeof performance !== 'undefined' ? performance.now() : this.now()) - t0
      let body: unknown = null
      try {
        body = await res.json()
      } catch {
        /* not JSON: gateway error page */
      }
      const isService = !!body && typeof body === 'object' && 'status' in (body as object)
      return { ok: res.ok && isService, status: isService ? res.status : 'unreachable', ms, body }
    } catch {
      return { ok: false, status: 'unreachable', ms: 0, body: null }
    }
  }

  private async raw<T>(method: string, url: string, opts: RequestOptions): Promise<T> {
    const requestId = this.newRequestId()
    const headers: Record<string, string> = { accept: 'application/json', 'x-request-id': requestId, ...opts.headers }
    let body: BodyInit | undefined
    if (opts.body !== undefined && method !== 'GET' && method !== 'HEAD') {
      if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer) { // a file upload: the bytes as they are
        headers['content-type'] = (opts.body instanceof Blob && opts.body.type) || 'application/octet-stream'
        body = opts.body
      } else {
        headers['content-type'] = 'application/json'
        body = JSON.stringify(opts.body)
      }
    }
    if (opts.auth !== false && this.session) headers.authorization = `Bearer ${this.session.accessToken}`
    const full = `${this.baseUrl}${url}${buildQuery(opts.query)}`
    let res: Response
    try {
      res = await this.fetchImpl(full, { method, headers, body, signal: opts.signal, credentials: 'same-origin', cache: 'no-store' })
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') {
        throw new ApiError({ status: 0, code: CLIENT_CODES.aborted, detail: 'Request cancelled.', requestId, method, path: url })
      }
      throw new ApiError({ status: 0, code: CLIENT_CODES.network, detail: '', requestId, method, path: url })
    }
    const rid = res.headers.get('x-request-id') ?? requestId
    if (res.status === 204) return undefined as T
    const text = await res.text()
    let data: unknown = undefined
    if (text) {
      try {
        data = JSON.parse(text)
      } catch {
        data = undefined
      }
    }
    if (res.ok) {
      if (data === undefined && text) throw new ApiError({ status: res.status, code: CLIENT_CODES.badResponse, detail: '', requestId: rid, method, path: url })
      return data as T
    }
    const p = (data && typeof data === 'object' ? data : null) as Problem | null
    // A FastAPI 404 for an unknown route ("Not Found") means the module isn't deployed yet.
    if (res.status === 404 && (!p || p.detail === 'Not Found')) {
      throw new ApiError({ status: 404, code: CLIENT_CODES.unavailable, detail: '', requestId: rid, problem: p, method, path: url })
    }
    // No problem body from a 5xx: the gateway/dev proxy couldn't reach the service.
    if (res.status >= 500 && (!p || !p.code)) {
      throw new ApiError({ status: res.status, code: CLIENT_CODES.unreachable, detail: '', requestId: rid, problem: p, method, path: url })
    }
    throw new ApiError({
      status: res.status,
      code: p?.code ?? `http_${res.status}`,
      detail: p?.detail ?? '',
      requestId: p?.request_id ?? rid,
      fields: Array.isArray(p?.fields) ? p!.fields : [],
      problem: p,
      method,
      path: url,
    })
  }
}

export function createClient(opts?: ClientOptions): ApiClient {
  return new ApiClient(opts)
}
